import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Zap, ArrowRight, Sparkles, Star, Type, Play, Wand2, FileText, Mail, MessageSquare,
  ShoppingBag, Megaphone, Film, Clapperboard, Video,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTypingAnimation } from "../hooks/useTypingAnimation";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { InteractiveDemo } from "../components/InteractiveDemo";
import SocialProofTicker from "../components/SocialProofTicker";

const HERO_WORDS = ["sounds like you.", "writes like you.", "hooks like you.", "posts like you."];

const HOW_IT_WORKS = [
  { s: "01", i: Type, t: "Train your Clone", d: "Paste 3–10 past posts. We extract your voice DNA in 30 seconds." },
  { s: "02", i: Play, t: "Pick a trend or idea", d: "Trend Radar shows what's breaking today + 3 post ideas ready for you." },
  { s: "03", i: Wand2, t: "Generate & post", d: "Every word sounds like you. Refine, copy, publish. Repeat daily." },
];

const FEATURES = [
  { i: FileText, t: "Blog Posts", d: "SEO articles that drive traffic", c: "text-blue-400" },
  { i: Megaphone, t: "Marketing Copy", d: "Ad copy that converts", c: "text-yellow-400" },
  { i: ShoppingBag, t: "Products", d: "E-commerce copy that sells", c: "text-green-400" },
  { i: MessageSquare, t: "Social Media", d: "Viral posts for any platform", c: "text-purple-400" },
  { i: Mail, t: "Email", d: "Sequences that convert", c: "text-red-400" },
  { i: Film, t: "TikTok Scripts", d: "Viral hooks + visual cues", c: "text-pink-400" },
  { i: Clapperboard, t: "Instagram Reels", d: "Reel scripts with hashtags", c: "text-orange-400" },
  { i: Video, t: "YouTube Shorts", d: "Attention-grabbing scripts", c: "text-cyan-400" },
];

const STATS = [
  { v: "10000", s: "+", l: "Active Writers" },
  { v: "2000000", s: "+", l: "Words Generated" },
  { v: "99", s: ".9%", l: "Uptime" },
  { v: "4", s: ".9/5", l: "Rating" },
];

const TESTIMONIALS = [
  { n: "Sarah Chen", r: "Founder, Bloom Digital", q: "CopyForge replaced our entire content team. ROI positive in week one." },
  { n: "Marcus Rodriguez", r: "E-commerce Owner", q: "Product descriptions in seconds. Conversion rate jumped 34% with AI Refiner." },
  { n: "Alex Kim", r: "Marketing Director", q: "Email campaigns are insane. Open rates doubled." },
];

const PRICING = [
  { n: "Try It", p: "$2", cr: "5", per: "$0.40", badge: "" },
  { n: "Starter", p: "$5", cr: "20", per: "$0.25", badge: "" },
  { n: "Pro", p: "$12", cr: "75", per: "$0.16", badge: "popular" },
  { n: "Business", p: "$25", cr: "200", per: "$0.13", badge: "best_value" },
  { n: "Agency", p: "$49", cr: "500", per: "$0.10", badge: "" },
];

const SUBS_MONTHLY = [
  { n: "Creator", p: "$49", per: "/mo", cr: "Unlimited Clone + Trends + Autopsy · 500 cr", sub: "The moat: every word in your voice.", badge: "best_value" },
  { n: "Pro Monthly", p: "$19", per: "/mo", cr: "300 cr / month", sub: "Auto-renews. Cancel anytime.", badge: "" },
  { n: "Developer API", p: "$99", per: "/mo", cr: "1,000 cr/mo + API access", sub: "For devs integrating CopyForge.", badge: "popular" },
];

const SUBS_ANNUAL = [
  { n: "Creator Annual", p: "$470", per: "/yr", cr: "6,000 credits upfront · Unlimited flagship tools", sub: "≈$39.20/mo · save 20%", badge: "best_value" },
  { n: "Pro Annual", p: "$182", per: "/yr", cr: "3,600 credits upfront", sub: "≈$15.20/mo · save 20%", badge: "" },
  { n: "Developer Annual", p: "$950", per: "/yr", cr: "12,000 cr + API access", sub: "≈$79.20/mo · save 20%", badge: "popular" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const typing = useTypingAnimation(HERO_WORDS, 80, 1800);
  const [billingMode, setBillingMode] = useState("monthly"); // monthly | annual | one_time

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SocialProofTicker />
      <nav className="fixed top-0 w-full backdrop-blur-xl bg-white/80 border-b border-zinc-200 z-50" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => nav("/")}>
            <Zap className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span className="font-black text-xl tracking-tighter text-zinc-950 font-heading">CopyForge</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("demo")} className="text-sm font-medium text-zinc-600 hover:text-zinc-950">Demo</button>
            <button onClick={() => scrollTo("features")} className="text-sm font-medium text-zinc-600 hover:text-zinc-950">Features</button>
            <button onClick={() => scrollTo("pricing")} className="text-sm font-medium text-zinc-600 hover:text-zinc-950">Pricing</button>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <button data-testid="go-to-dashboard-btn" onClick={() => nav("/dashboard")} className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-brand-hover transition-all hover:shadow-lg hover:shadow-blue-500/20">Dashboard</button>
            ) : (
              <>
                <button data-testid="login-nav-btn" onClick={() => nav("/login")} className="text-zinc-700 font-semibold px-4 py-2 hover:text-zinc-950">Log In</button>
                <button data-testid="signup-nav-btn" onClick={() => nav("/register")} className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-brand-hover transition-all hover:shadow-lg hover:shadow-blue-500/20">Start Free</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-20 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto relative" data-testid="hero-section">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-brand/[0.03] rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 px-4 py-2 mb-8 animate-fade-in">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold tracking-[0.15em] text-zinc-600 uppercase">The only AI that sounds like you</span>
          </div>
          <button
            onClick={() => nav("/founders")}
            data-testid="hero-founders-strip"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-zinc-950 px-3 py-1.5 mb-6 text-xs font-bold tracking-wide uppercase hover:scale-[1.02] transition-transform animate-fade-in"
            style={{ animationDelay: "0.05s" }}
          >
            🔥 Founders Lifetime — $97 once, never pay again · First 100 only →
          </button>
          <button
            onClick={() => nav(user ? "/business/ads" : "/register?intent=business")}
            data-testid="hero-business-strip"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-3 py-1.5 mb-6 ml-2 text-xs font-bold tracking-wide uppercase hover:bg-zinc-800 transition-colors animate-fade-in"
            style={{ animationDelay: "0.06s" }}
          >
            💼 New: Multi-platform Business Ads — 1 offer → 8 platforms →
          </button>
          <h1 className="text-5xl md:text-6xl lg:text-[5.5rem] font-black tracking-tighter leading-[1.05] text-zinc-950 mb-8 font-heading animate-fade-in" style={{ animationDelay: "0.1s" }}>
            An AI that<br />
            <span className="text-brand inline-block min-w-[300px]">{typing}<span className="animate-blink ml-0.5">|</span></span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-zinc-500 mb-10 max-w-xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
            Train your <strong className="text-zinc-700">Voice Clone</strong> in 30 seconds, see what's breaking today on <strong className="text-zinc-700">Trend Radar</strong>, and dissect any viral post with <strong className="text-zinc-700">Viral Autopsy</strong>. Every word written in your voice. Not generic AI slop.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <button data-testid="hero-cta-btn" onClick={() => nav(user ? "/dashboard" : "/register")} className="group bg-brand text-white font-semibold px-8 py-4 hover:bg-brand-hover transition-all flex items-center gap-2 text-lg hover:shadow-xl hover:shadow-blue-500/20 hover:-translate-y-0.5">
              Start Writing Free <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="hero-demo-cta-btn" onClick={() => nav("/try")} className="group border border-zinc-300 text-zinc-900 font-semibold px-8 py-4 hover:border-zinc-950 hover:bg-zinc-50 transition-all flex items-center gap-2 text-lg">
              Try the free demo — no signup
            </button>
          </div>
          <div className="flex items-center gap-3 text-zinc-500 mt-6">
            <div className="flex -space-x-2">
              {["A", "B", "C", "D"].map((letter) => (
                <div key={letter} className="w-8 h-8 bg-zinc-200 border-2 border-white rounded-full flex items-center justify-center text-xs font-bold text-zinc-500">{letter}</div>
              ))}
            </div>
            <span className="text-sm font-medium">Join 10,000+ creators</span>
          </div>
        </div>
      </section>

      <section id="demo" className="py-20 px-6 md:px-12 lg:px-24 bg-zinc-50 border-y border-zinc-200" data-testid="demo-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-medium tracking-[0.2em] text-zinc-500 uppercase mb-3">See It In Action</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 font-heading">Watch CopyForge write a blog post.</h2>
          </div>
          <InteractiveDemo />
        </div>
      </section>

      <section className="py-10 px-6 md:px-12 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs font-bold tracking-[0.25em] text-zinc-400 uppercase mb-6">Trusted by teams at</p>
          <div className="flex items-center justify-center gap-12 opacity-40 flex-wrap">
            {["Vercel", "Stripe", "Linear", "Notion", "Figma", "Shopify"].map((n) => (
              <span key={n} className="text-xl font-black tracking-tight text-zinc-950 font-heading">{n}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-medium tracking-[0.2em] text-zinc-500 uppercase mb-4">How It Works</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 mb-16 font-heading">Three steps to revenue-driving copy.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map(({ s, i: Icon, t, d }) => (
              <div key={s} className="relative group" data-testid={`step-${s}`}>
                <div className="text-6xl font-black text-zinc-100 font-heading mb-4 group-hover:text-brand/10 transition-colors">{s}</div>
                <div className="w-12 h-12 bg-zinc-950 flex items-center justify-center mb-5 group-hover:bg-brand transition-colors">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-zinc-900 mb-3 font-heading">{t}</h3>
                <p className="text-zinc-500 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-24 px-6 md:px-12 lg:px-24 bg-zinc-950" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-medium tracking-[0.2em] text-zinc-400 uppercase mb-4">Templates</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-12 font-heading">Eight ways to make money while you sleep.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {FEATURES.map(({ i: Icon, t, d, c }) => (
              <div key={t} className="border border-zinc-800 p-6 bg-zinc-900/50 hover:bg-zinc-900 transition-all hover:border-zinc-700 group" data-testid={`feature-card-${t}`}>
                <Icon className={`w-8 h-8 ${c} mb-4 group-hover:scale-110 transition-transform`} strokeWidth={1.5} />
                <h3 className="text-lg font-bold text-white mb-2 font-heading">{t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="border border-zinc-800 bg-zinc-900/50 p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="refine-feature-card">
              <div className="w-14 h-14 bg-brand flex items-center justify-center flex-shrink-0"><Wand2 className="w-7 h-7 text-white" /></div>
              <div>
                <span className="text-xs font-bold tracking-[0.15em] text-brand uppercase mb-2 block">AI Refiner</span>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">Perfect Any Output</h3>
                <p className="text-zinc-400 leading-relaxed">Make it shorter, more persuasive, add humor — just 1 credit per refinement.</p>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-900/50 p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="video-feature-card">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0"><Video className="w-7 h-7 text-white" /></div>
              <div>
                <span className="text-xs font-bold tracking-[0.15em] text-pink-400 uppercase mb-2 block">Video Studio</span>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">Sora 2 AI Video</h3>
                <p className="text-zinc-400 leading-relaxed">Generate TikTok, Reels, and YouTube Shorts videos from text. AI-powered video creation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {STATS.map(({ v, s, l }) => (
            <div key={l} className="text-center" data-testid={`stat-${l}`}>
              <div className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950 mb-1 font-heading">
                <AnimatedCounter target={v} suffix={s} />
              </div>
              <div className="text-xs font-bold tracking-[0.2em] text-zinc-400 uppercase">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-zinc-50 border-y border-zinc-200" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-medium tracking-[0.2em] text-zinc-500 uppercase mb-4">Testimonials</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 mb-12 font-heading">What our users say.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ n, r, q }) => (
              <div key={n} className="bg-white border border-zinc-200 p-8 hover:-translate-y-1 transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`testimonial-${n}`}>
                <div className="flex gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                  ))}
                </div>
                <p className="text-zinc-700 leading-relaxed mb-6 text-[15px]">"{q}"</p>
                <div>
                  <p className="font-bold text-zinc-900 text-sm">{n}</p>
                  <p className="text-xs text-zinc-500">{r}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 px-6 md:px-12 lg:px-24 bg-zinc-950" data-testid="pricing-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-sm font-medium tracking-[0.2em] text-zinc-400 uppercase mb-4">Pricing</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4 font-heading">Three ways to pay. All of them profitable.</h2>
            <p className="text-zinc-400 max-w-lg mx-auto">10 free credits on signup. Monthly plans for power users. Annual for the biggest savings.</p>
          </div>

          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-800 p-1" data-testid="landing-billing-toggle">
              {[
                { id: "monthly", label: "Monthly" },
                { id: "annual", label: "Annual" },
                { id: "one_time", label: "One-time" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setBillingMode(id)}
                  data-testid={`landing-billing-${id}`}
                  className={`relative px-4 py-2 text-sm font-semibold transition-all ${billingMode === id ? "bg-brand text-white" : "text-zinc-400 hover:text-white"}`}
                >
                  {label}
                  {id === "annual" && <span className="ml-2 text-[10px] font-bold bg-green-500 text-white px-1.5 py-0.5">-20%</span>}
                </button>
              ))}
            </div>
          </div>

          {billingMode === "one_time" && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-6xl mx-auto animate-fade-in" data-testid="pricing-onetime-grid">
              {PRICING.map((pkg) => (
                <div key={pkg.n} className={`border p-6 relative ${pkg.badge === "popular" ? "border-brand bg-zinc-900 ring-1 ring-brand" : "border-zinc-800 bg-zinc-900/50"}`} data-testid={`pricing-card-${pkg.n}`}>
                  {pkg.badge === "popular" && <div className="absolute -top-3 left-4 bg-brand text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Most Popular</div>}
                  {pkg.badge === "best_value" && <div className="absolute -top-3 left-4 bg-green-500 text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Best Value</div>}
                  <h3 className="text-lg font-bold text-white mb-1 font-heading">{pkg.n}</h3>
                  <div className="text-3xl font-black text-white mb-0.5 tracking-tighter font-heading">{pkg.p}</div>
                  <p className="text-zinc-400 text-xs mb-4">{pkg.cr} cr &middot; {pkg.per}/cr</p>
                  <button data-testid={`pricing-cta-${pkg.n}`} onClick={() => nav(user ? "/pricing" : "/register")} className={`w-full py-2.5 text-sm font-semibold transition-all ${pkg.badge === "popular" ? "bg-brand text-white hover:bg-brand-hover" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>Get Started</button>
                </div>
              ))}
            </div>
          )}

          {(billingMode === "monthly" || billingMode === "annual") && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto animate-fade-in" data-testid={`pricing-${billingMode}-grid`}>
              {(billingMode === "monthly" ? SUBS_MONTHLY : SUBS_ANNUAL).map((pkg) => (
                <div key={pkg.n} className={`border p-8 relative ${pkg.badge === "popular" ? "border-brand bg-zinc-900 ring-1 ring-brand" : "border-zinc-800 bg-zinc-900/50"}`} data-testid={`sub-pricing-${pkg.n}`}>
                  {pkg.badge === "popular" && <div className="absolute -top-3 left-4 bg-brand text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Most Popular</div>}
                  {pkg.badge === "best_value" && <div className="absolute -top-3 left-4 bg-green-500 text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Best Value</div>}
                  <h3 className="text-xl font-bold text-white mb-2 font-heading">{pkg.n}</h3>
                  <div className="flex items-end gap-1 mb-2">
                    <span className="text-4xl font-black text-white tracking-tighter font-heading">{pkg.p}</span>
                    <span className="text-zinc-400 text-sm font-medium pb-1.5">{pkg.per}</span>
                  </div>
                  <p className="text-zinc-300 text-sm mb-1">{pkg.cr}</p>
                  <p className="text-zinc-500 text-xs mb-6">{pkg.sub}</p>
                  <button data-testid={`sub-pricing-cta-${pkg.n}`} onClick={() => nav(user ? "/subscription" : "/register")} className={`w-full py-2.5 text-sm font-semibold transition-all ${pkg.badge === "popular" ? "bg-brand text-white hover:bg-brand-hover" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}`}>
                    {user ? "Subscribe" : "Start Free"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-950 mb-6 font-heading">Ready to write copy that converts?</h2>
          <p className="text-lg text-zinc-500 mb-8">10 free credits. No card required.</p>
          <button data-testid="final-cta-btn" onClick={() => nav(user ? "/dashboard" : "/register")} className="group bg-zinc-950 text-white font-semibold px-10 py-4 hover:bg-brand transition-all text-lg flex items-center gap-2 mx-auto hover:shadow-xl hover:shadow-blue-500/20">
            Start Writing Free <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <footer className="py-8 px-6 md:px-12 border-t border-zinc-200 bg-zinc-50" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-brand" strokeWidth={2.5} />
            <span className="font-bold text-zinc-950 tracking-tighter font-heading">CopyForge</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => nav("/terms")} className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-terms">Terms</button>
            <button onClick={() => nav("/privacy")} className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="footer-privacy">Privacy</button>
            <p className="text-sm text-zinc-500">&copy; {new Date().getFullYear()} CopyForge LLC</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
